import { applyPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, FinalCTA, Breadcrumbs, CTAButton, BOOKING_URL } from "@/components/site/Layout";

type Guide = {
  file: string;
  title: string;
  description: string;
  pages: number;
  category: string;
  highlights: string[];
};

const guides: Guide[] = [
  {
    file: "law-firm-seo-strategy-2026.pdf",
    title: "Law Firm SEO Strategy: The 2026 Playbook",
    description: "A partner-level guide to modern legal SEO, architecture, E-E-A-T, AI Overviews, and a 12-month sequencing plan.",
    pages: 6,
    category: "Strategy",
    highlights: ["Four-layer SEO stack", "Revenue-based keyword tiering", "12-month rollout timeline"],
  },
  {
    file: "local-seo-for-law-firms-checklist.pdf",
    title: "Local SEO for Law Firms: 40-Point Checklist",
    description: "The exact GBP, citation, review, and local-pack checklist we run for every firm we onboard.",
    pages: 5,
    category: "Local SEO",
    highlights: ["GBP setup", "Citation & NAP audit", "Review-velocity workflow"],
  },
  {
    file: "dui-dwi-lawyer-seo-guide.pdf",
    title: "DUI / DWI Lawyer SEO: The Complete Guide",
    description: "A niche playbook for defense attorneys, local pack, mobile-first intake, and 2 a.m. conversion.",
    pages: 5,
    category: "Practice Area",
    highlights: ["Modifier keyword universe", "24/7 intake blueprint", "Content that ranks AND reassures"],
  },
  {
    file: "personal-injury-seo-blueprint.pdf",
    title: "Personal Injury Lawyer SEO Blueprint",
    description: "How PI firms build defensible organic pipelines in the most competitive vertical in legal SEO.",
    pages: 5,
    category: "Practice Area",
    highlights: ["Sub-practice architecture", "E-E-A-T signals PI clients look for", "Ethical link strategies"],
  },
  {
    file: "google-business-profile-attorneys.pdf",
    title: "Google Business Profile Optimization for Attorneys",
    description: "The 60-minute weekly playbook that keeps your GBP dominating the local pack.",
    pages: 5,
    category: "Local SEO",
    highlights: ["Weekly posting cadence", "Review response templates", "Messaging & Q&A setup"],
  },
  {
    file: "link-building-for-law-firms.pdf",
    title: "Link Building for Law Firms: The Ethical Playbook",
    description: "How to earn authority links in a vertical Google penalizes aggressively for manipulation.",
    pages: 5,
    category: "Authority",
    highlights: ["What NOT to do", "Local editorial link tactics", "Digital PR for attorneys"],
  },
];

export const Route = createFileRoute("/guides")({
  head: () => applyPageMetadata("/guides", {
    meta: [
      { title: "Free PDF Guides for Law Firm SEO" },
      { name: "description", content: "Download free PDF guides on law firm SEO strategy, local SEO, GBP optimization, DUI SEO, personal injury SEO, and link building, written by Aditya Chauhan." },
      { property: "og:title", content: "Free PDF Guides for Law Firm SEO" },
      { property: "og:description", content: "Free, no-email-required downloadable PDF guides for law firm marketing partners and attorneys." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://pageonelawyers.com/guides" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://pageonelawyers.com/guides" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Free PDF Guides for Law Firm SEO",
          url: "https://legal-leads-lounge.lovable.app/guides",
          hasPart: guides.map((g) => ({
            "@type": "DigitalDocument",
            name: g.title,
            description: g.description,
            encodingFormat: "application/pdf",
            url: `https://legal-leads-lounge.lovable.app/guides/${g.file}`,
            author: { "@type": "Person", name: "Aditya Chauhan" },
          })),
        }),
      },
    ],
  }),
  component: GuidesPage,
});

function GuidesPage() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:px-6 sm:py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Guides" }]} />
          <Eyebrow><span className="text-[var(--gold)]">PDF Guides</span></Eyebrow>
          <h1 className="mt-6 font-display text-4xl font-semibold text-primary-foreground sm:text-5xl">Free PDF guides for law firm SEO.</h1><LastUpdated />
          <div className="mt-6 flex max-w-3xl flex-col items-start gap-4 sm:flex-row">
            <img
              src="/__l5e/assets-v1/49542b44-d142-44e5-b009-d029a898f033/aditya-chauhan.png"
              alt="Aditya Chauhan, Head of SEO at PageOne Lawyers"
              width={64}
              height={64}
              loading="lazy"
              className="h-16 w-16 flex-shrink-0 rounded-full object-cover border border-[var(--gold)]/40 bg-white"
            />
            <p className="text-xl text-primary-foreground/85">Downloadable playbooks written by <a href="/about" className="text-[var(--gold)] font-semibold hover:underline">Aditya Chauhan</a>. Resources for law-firm owners. No email gate, no signup, just the same frameworks we use in paid engagements.</p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-2 gap-6">
          {guides.map((g) => (
            <article key={g.file} className="flex min-w-0 flex-col rounded-lg border border-border bg-card p-5 transition hover:border-[var(--gold)] sm:p-8">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">{g.category}</span>
                <span className="text-xs text-muted-foreground">{g.pages} pages · PDF</span>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-foreground">{g.title}</h2>
              <p className="mt-3 text-muted-foreground">{g.description}</p>
              <ul className="mt-5 space-y-2">
                {g.highlights.map((h) => (
                  <li key={h} className="flex gap-2 text-sm text-foreground/80"><span className="text-[var(--gold-deep)]">✓</span>{h}</li>
                ))}
              </ul>
              <div className="mt-auto flex flex-col gap-3 pt-6 sm:flex-row sm:flex-wrap">
                <a
                  href={`/guides/${g.file}`}
                  download
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[var(--gold)] px-5 py-3 text-sm font-semibold text-[var(--navy-deep)] transition hover:bg-[var(--gold-deep)]"
                >
                  Download PDF
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0l-5-5m5 5l5-5M5 21h14"/></svg>
                </a>
                <a href={`/guides/${g.file}`} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold text-foreground transition hover:border-[var(--gold)]">Preview</a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-lg border border-border bg-secondary/40 p-5 text-center sm:mt-16 sm:p-10">
          <Eyebrow>Need something custom?</Eyebrow>
          <h2 className="mt-4 text-2xl font-semibold text-foreground sm:text-3xl">Want a guide built for your firm's specific market?</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">We'll assemble a custom SEO briefing PDF for your practice area and city, with keyword opportunity, competitor gaps, and a 90-day plan.</p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="/law-firm-seo-audit" variant="gold">Request a custom audit</CTAButton>
            <CTAButton to={BOOKING_URL}>Talk to our team</CTAButton>
          </div>
        </div>
      </Section>

      <FinalCTA />
    </Layout>
  );
}